import React, { useEffect, useState } from 'react';
import {
  DndContext,
  closestCenter,
  DragEndEvent,
  useSensor,
  useSensors,
  PointerSensor,
  TouchSensor,
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { Sun, Moon, Plus, Sparkle, Check, X, Warning } from 'phosphor-react';
import ActionModal from './ActionModal';
import SortableStepCard from '../cards/SortableStepCard';
import CategoryPicker from '../inputs/CategoryPicker';
import Aviso from '../ui/Aviso';
import EstadoVazio from '../ui/EstadoVazio';
import usePresence from '../../use/usePresence';
import StyledInput from '../inputs/StyledInput';
import PrimaryButton from '../buttons/PrimaryButton';
import RoutineService from '../../api/RoutineService';
import { useCurrentUser } from '../../use/useCurrentUser';
import useProductCategories from '../../use/useProductCategories';
import { getApiErrorMessage } from '../../utils/http';
import { useToast } from '../../context/ToastContext';
import { Routine } from '../../types/entities';
import { Period } from '../../types/enums';

export interface DraftStep {
  _localId: number;
  productCategoryId: string;
  productCategoryName: string;
  instructions: string;
}

export interface DraftRoutine {
  period: Period;
  steps: DraftStep[];
}

interface RoutineSuggestionModalProps {
  isOpen: boolean;
  /** Rotinas recomendadas pela IA (nada foi salvo ainda). */
  routines: Routine[];
  /**
   * Aviso da IA quando o relato do usuário aponta algo que precisa de um
   * profissional de saúde. Fica no topo do modal — antes dos passos, de propósito.
   */
  professionalAlert?: string | null;
  /** Chamado depois que as rotinas aprovadas foram salvas com sucesso. */
  onSaved: () => void;
  onDiscard: () => void;
}

const TABS = [
  { period: Period.MORNING, label: 'Manhã', Icon: Sun, activeClass: 'bg-blush-500 text-white shadow-glow-blush' },
  { period: Period.NIGHT, label: 'Noite', Icon: Moon, activeClass: 'bg-lavender-500 text-white shadow-lg' },
];

/** Sempre monta os dois períodos: o que a IA não sugeriu fica vazio para o usuário montar. */
function toDrafts(routines: Routine[]): DraftRoutine[] {
  return TABS.map(({ period }) => {
    const found = routines.find((r) => r.period === period);
    const steps = (found?.steps ?? [])
      .slice()
      .sort((a, b) => a.stepOrder - b.stepOrder)
      .map((s, i) => ({
        _localId: i,
        productCategoryId: s.productCategoryId,
        productCategoryName: s.productCategoryName,
        instructions: s.instructions ?? '',
      }));
    return { period, steps };
  });
}

export default function RoutineSuggestionModal({
  isOpen,
  routines,
  professionalAlert = null,
  onSaved,
  onDiscard,
}: RoutineSuggestionModalProps) {
  const { userId } = useCurrentUser();
  const toast = useToast();
  const [drafts, setDrafts] = useState<DraftRoutine[]>([]);
  const [activePeriod, setActivePeriod] = useState<Period>(Period.MORNING);
  // Mesma chave (e mesmo cache) do editor de rotina e da biblioteca de produtos.
  const { categories, loading: carregandoCategorias } = useProductCategories();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [adding, setAdding] = useState(false);
  const formulario = usePresence(adding, 180);
  const alerta = usePresence(Boolean(professionalAlert), 180);
  const [newCategory, setNewCategory] = useState<{ id: string; name: string }>({ id: '', name: '' });
  const [newInstructions, setNewInstructions] = useState('');

  useEffect(() => setDrafts(toDrafts(routines)), [routines]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 5 } })
  );

  const steps = drafts.find((d) => d.period === activePeriod)?.steps ?? [];

  const updateSteps = (next: DraftStep[]) =>
    setDrafts((prev) => prev.map((d) => (d.period === activePeriod ? { ...d, steps: next } : d)));

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    const oldIndex = steps.findIndex((s) => s._localId === active.id);
    const newIndex = steps.findIndex((s) => s._localId === over.id);
    updateSteps(arrayMove(steps, oldIndex, newIndex));
  };

  const resetAddForm = () => {
    setAdding(false);
    setNewCategory({ id: '', name: '' });
    setNewInstructions('');
  };

  const handleAddStep = () => {
    if (!newCategory.id) return;
    // ponytail: _localId por contador simples — só precisa ser único dentro do modal.
    const nextId = Math.max(-1, ...steps.map((s) => s._localId)) + 1;
    updateSteps([
      ...steps,
      {
        _localId: nextId,
        productCategoryId: newCategory.id,
        productCategoryName: newCategory.name,
        instructions: newInstructions.trim(),
      },
    ]);
    resetAddForm();
  };

  const totalSteps = drafts.reduce((acc, d) => acc + d.steps.length, 0);

  /** Salva só os períodos com etapas — um POST por período (o backend faz upsert). */
  const handleSave = async () => {
    const toSave = drafts.filter((d) => d.steps.length > 0);
    if (toSave.length === 0) return;

    setSaving(true);
    setError('');
    try {
      for (const draft of toSave) {
        await RoutineService.createRoutine(userId!, {
          period: draft.period,
          steps: draft.steps.map((s, i) => ({
            stepOrder: i + 1,
            productCategoryId: s.productCategoryId,
            instructions: s.instructions,
          })),
        });
      }
      onSaved();
      toast.sucesso('Rotina salva.');
    } catch (err) {
      const msg = getApiErrorMessage(err, 'Erro ao salvar a rotina. Tente novamente.');
      setError(msg);
      toast.erro(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <ActionModal isOpen={isOpen} onClose={onDiscard} size="lg">
      <div className="flex flex-col gap-4">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 w-11 h-11 bg-blush-50 dark:bg-blush-900/30 rounded-2xl flex items-center justify-center">
            <Sparkle className="w-5 h-5 text-blush-400" />
          </div>
          <div className="flex-1">
            <h2 className="text-lg font-bold text-gray-800 dark:text-gray-100">Recomendação da IA</h2>
            <p className="text-xs text-nude-500 dark:text-gray-400 mt-0.5 leading-relaxed">
              Esta é uma sugestão, não uma regra. Remova, adicione ou reordene as etapas
              — só é salvo quando você confirmar.
            </p>
          </div>
          <button
            onClick={onDiscard}
            className="flex-shrink-0 p-1.5 text-nude-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-nude-100 dark:hover:bg-gray-700 rounded-xl transition-colors"
            aria-label="Descartar recomendação"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {alerta.montado && (
          <div
            data-state={alerta.estado}
            className="flex items-start gap-3 p-4 bg-amber-50 dark:bg-amber-900/20 border-2 border-amber-200 dark:border-amber-700 rounded-2xl data-[state=open]:animate-enter data-[state=closed]:animate-exit"
          >
            <Warning className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-amber-700 dark:text-amber-300">
                Vale procurar um profissional
              </p>
              <p className="text-xs text-amber-600 dark:text-amber-400 mt-1 leading-relaxed">
                {professionalAlert}
              </p>
            </div>
          </div>
        )}

        <div className="bg-nude-50 dark:bg-gray-900 rounded-2xl p-1.5 flex">
          {TABS.map(({ period, label, Icon, activeClass }) => {
            const count = drafts.find((d) => d.period === period)?.steps.length ?? 0;
            return (
              <button
                key={period}
                onClick={() => { setActivePeriod(period); resetAddForm(); }}
                className={`
                  flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold transition-colors duration-300 ease-soft
                  ${activePeriod === period ? activeClass : 'text-nude-400 dark:text-gray-400 hover:text-gray-600 dark:hover:text-gray-200'}
                `}
              >
                <Icon className="w-4 h-4" />
                {label}
                <span className={`text-xs font-bold ${activePeriod === period ? 'opacity-80' : 'opacity-60'}`}>
                  ({count})
                </span>
              </button>
            );
          })}
        </div>

        {/* `key={activePeriod}` para a troca Manhã ↔ Noite não ser um corte seco.
            A lista em si não escalona: o dnd-kit já escreve transform/transition
            inline em cada item e as duas coisas brigariam. */}
        <div key={activePeriod} className="animate-enter max-h-[42vh] overflow-y-auto pr-0.5 -mr-0.5">
          {steps.length > 0 ? (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={steps.map((s) => s._localId)} strategy={verticalListSortingStrategy}>
                <div className="flex flex-col gap-2.5">
                  {steps.map((step, index) => (
                    <SortableStepCard
                      key={step._localId}
                      id={step._localId}
                      stepOrder={index + 1}
                      productCategoryName={step.productCategoryName}
                      instructions={step.instructions}
                      onRemove={() => updateSteps(steps.filter((s) => s._localId !== step._localId))}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          ) : (
            <EstadoVazio
              className="py-8 bg-nude-50 dark:bg-gray-900 rounded-2xl"
              icone={<Plus className="w-8 h-8 text-nude-300 dark:text-gray-600" />}
              titulo="Nenhuma etapa neste período"
              descricao="Adicione abaixo ou deixe vazio"
            />
          )}

          {formulario.montado ? (
            <div
              data-state={formulario.estado}
              className="mt-3 p-3 bg-nude-50 dark:bg-gray-900 rounded-2xl flex flex-col gap-3 data-[state=open]:animate-enter data-[state=closed]:animate-exit"
            >
              <CategoryPicker
                categories={categories}
                carregando={carregandoCategorias}
                selectedId={newCategory.id}
                selectedIsCustom={false}
                onSelect={(cat) => setNewCategory({ id: cat.id, name: cat.name })}
              />
              <StyledInput
                label="Instruções (opcional)"
                name="newStepInstructions"
                value={newInstructions}
                onChange={(e) => setNewInstructions(e.target.value)}
                placeholder="Ex: Aplicar no rosto úmido e massagear"
              />
              <div className="flex gap-2">
                <button
                  onClick={resetAddForm}
                  className="flex-1 py-2.5 text-xs font-medium text-nude-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 rounded-xl transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleAddStep}
                  disabled={!newCategory.id}
                  className="flex-1 py-2.5 text-xs font-semibold text-white bg-blush-500 hover:bg-blush-600 disabled:opacity-50 rounded-xl transition-colors active:scale-[0.97] disabled:active:scale-100"
                >
                  Adicionar etapa
                </button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setAdding(true)}
              className="mt-3 w-full flex items-center justify-center gap-2 py-3 border-2 border-dashed border-nude-200 dark:border-gray-700 rounded-2xl text-sm text-nude-400 dark:text-gray-500 font-medium hover:border-blush-300 hover:text-blush-500 hover:bg-blush-50/30 dark:hover:border-blush-700 dark:hover:text-blush-400 transition-colors active:scale-[0.97]"
            >
              <Plus className="w-4 h-4" />
              Adicionar etapa
            </button>
          )}
        </div>

        <Aviso mensagem={error} />

        <div className="flex flex-col gap-2">
          <PrimaryButton
            fullWidth
            loading={saving}
            disabled={totalSteps === 0}
            onClick={handleSave}
            icon={<Check className="w-4 h-4" />}
          >
            Salvar minha rotina
          </PrimaryButton>
          <button
            onClick={onDiscard}
            disabled={saving}
            className="w-full py-3 rounded-2xl border border-nude-200 dark:border-gray-600 text-sm font-medium text-nude-500 dark:text-gray-400 hover:bg-nude-50 dark:hover:bg-gray-700 disabled:opacity-50 transition-colors"
          >
            Descartar sugestão
          </button>
        </div>
      </div>
    </ActionModal>
  );
}
